import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const ShutdownEffect = ({ isShuttingDown, onComplete }) => {
  const [phase, setPhase] = useState('idle'); // idle, shrinking, flash, black, complete
  
  useEffect(() => {
    if (!isShuttingDown) {
      setPhase('idle');
      return;
    }

    const sequence = async () => {
      // Phase 1: Screen shrinks vertically
      setPhase('shrinking');
      
      // Phase 2: White flash after shrinking
      setTimeout(() => {
        setPhase('flash');
      }, 1000);
      
      // Phase 3: Fade to black
      setTimeout(() => {
        setPhase('black');
      }, 1200);
      
      // Phase 4: Show welcome back message and complete
      setTimeout(() => {
        setPhase('complete');
      }, 2500);
      
      // Phase 5: Clean up and return to normal
      setTimeout(() => {
        onComplete();
      }, 4000);
    };

    sequence();
  }, [isShuttingDown, onComplete]);

  if (!isShuttingDown && phase === 'idle') {
    return null;
  }

  return (
    <AnimatePresence>
      {phase !== 'idle' && (
        <div className="fixed inset-0 z-[99999] pointer-events-none">
          {/* Shrinking effect overlay */}
          {phase === 'shrinking' && (
            <motion.div
              className="absolute inset-0 bg-black"
              initial={{ 
                clipPath: 'inset(0% 0% 0% 0%)',
                filter: 'brightness(1)' 
              }}
              animate={{ 
                clipPath: 'inset(49.8% 0% 49.8% 0%)',
                filter: 'brightness(1.2)' 
              }}
              transition={{ 
                duration: 1,
                ease: [0.25, 0.46, 0.45, 0.94] // Ease out quad
              }}
            />
          )}

          {/* White flash effect */}
          {phase === 'flash' && (
            <motion.div
              className="absolute inset-0 bg-white"
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 1, 0] }}
              transition={{ 
                duration: 0.2,
                times: [0, 0.1, 1],
                ease: "easeInOut"
              }}
            />
          )}

          {/* Black screen */}
          {(phase === 'black' || phase === 'complete') && (
            <motion.div
              className="absolute inset-0 bg-black flex items-center justify-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              {/* Welcome back message */}
              {phase === 'complete' && (
                <motion.div
                  className="text-center"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ 
                    duration: 0.8,
                    ease: [0.25, 0.46, 0.45, 0.94]
                  }}
                >
                  <div className="text-4xl font-bold text-pink-400 mb-4">
                    Pink Pixel OS
                  </div>
                  <div className="text-xl text-white/80 mb-2">
                    Just kidding! 😄
                  </div>
                  <div className="text-lg text-white/60">
                    Welcome back to your fabulous desktop! ✨
                  </div>
                  
                  {/* Cute loading dots */}
                  <div className="flex justify-center gap-2 mt-6">
                    {[0, 1, 2].map((i) => (
                      <motion.div
                        key={i}
                        className="w-2 h-2 bg-pink-500 rounded-full"
                        animate={{ 
                          opacity: [0.3, 1, 0.3],
                          scale: [0.8, 1.2, 0.8]
                        }}
                        transition={{ 
                          duration: 1.5,
                          repeat: Infinity,
                          delay: i * 0.2,
                          ease: "easeInOut"
                        }}
                      />
                    ))}
                  </div>
                </motion.div>
              )}
            </motion.div>
          )}

          {/* CRT scanline effect for extra authenticity */}
          {phase === 'shrinking' && (
            <div className="absolute inset-0 pointer-events-none opacity-20">
              <div 
                className="w-full h-full"
                style={{
                  background: `repeating-linear-gradient(
                    0deg,
                    transparent,
                    transparent 2px,
                    rgba(255, 255, 255, 0.1) 2px,
                    rgba(255, 255, 255, 0.1) 4px
                  )`
                }}
              />
            </div>
          )}
        </div>
      )}
    </AnimatePresence>
  );
};

export default ShutdownEffect;
